import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useReducedMotion } from 'motion/react';
import { calculatePayPeriod, calculateSalary } from '@amigodoclt/core';
import { Icon } from '../components/Icon.js';
import { Segmented } from '../components/Field.js';
import { amountOf, money } from '../lib/format.js';
import { family, tools } from '../lib/calculators.js';
import { gsap, useReveal, useSpotlight, useToolHover } from '../lib/motion.js';

const guarantees = [
  { icon: 'person_off', label: 'Sem cadastro' },
  { icon: 'lock', label: 'Nada é salvo' },
  { icon: 'verified', label: 'Fontes citadas' },
];

export function Hub() {
  const root = useRef<HTMLDivElement>(null);
  const card = useRef<HTMLDivElement>(null);
  const proxy = useRef({ value: 5000 });
  const [target, setTarget] = useState(5000);
  const [shown, setShown] = useState(5000);
  const [period, setPeriod] = useState<'monthly' | 'biweekly'>('monthly');
  const reduce = useReducedMotion();

  useReveal(root);
  useSpotlight(card);
  useToolHover(root);

  // O valor exibido persegue o slider; INSS, IRRF e líquido são recalculados a cada quadro.
  useEffect(() => {
    if (reduce) {
      proxy.current.value = target;
      setShown(target);
      return;
    }
    const tween = gsap.to(proxy.current, {
      value: target, duration: .45, ease: 'power2.out', overwrite: true,
      onUpdate: () => setShown(proxy.current.value),
    });
    return () => { tween.kill(); };
  }, [target, reduce]);

  const result = useMemo(
    () => (period === 'biweekly'
      ? { biweekly: true as const, data: calculatePayPeriod({ competence: 2026, grossSalary: shown }) }
      : { biweekly: false as const, data: calculateSalary({ competence: 2026, grossSalary: shown }) }),
    [shown, period],
  );

  return (
    <div ref={root}>
      <section className="hub-top">
        <div className="hub-copy">
          <p className="eyebrow" data-reveal>Funcionalidades</p>
          {/* No mobile o primeiro <br> some (pages.css) e a linha vira "Do bruto ao líquido," — daí o espaço. */}
          <h1 className="page-title hub" data-reveal>{'Do bruto '}<br />ao líquido,<br /><em>sem letra miúda.</em></h1>
          <p className="lead" data-reveal>Dez ferramentas com a mesma base de regras da competência 2026. Cada resultado mostra a memória de cálculo.</p>
          <div className="hub-chips" data-reveal>
            {guarantees.map((item) => (
              <span className="chip info" key={item.label}><Icon name={item.icon} size={16} />{item.label}</span>
            ))}
          </div>
        </div>

        <div ref={card} className="hub-card glass-hero spotlight" data-reveal>
          <div className="hub-card-head">
            <span className="hub-card-id">
              <span className="hub-card-icon"><Icon name="payments" size={28} fill /></span>
              <span className="hub-card-title">
                <span className="eyebrow plain">Principal</span>
                <strong>Salário líquido</strong>
              </span>
            </span>
            <Link to="/calculos/salario" className="round-arrow" aria-label="Abrir a calculadora de salário líquido">
              <Icon name="arrow_forward" size={22} />
            </Link>
          </div>

          <Segmented
            label="Periodicidade"
            value={period}
            onChange={setPeriod}
            options={[{ value: 'monthly', label: 'Mensal', icon: 'calendar_month' }, { value: 'biweekly', label: 'Quinzenal', icon: 'event_repeat' }]}
            variant="auto"
          />

          <div className="hub-slider">
            <div className="hub-base">
              <span>Salário-base mensal</span>
              <strong>{money(shown)}</strong>
            </div>
            <input
              type="range" min={1518} max={20000} step={10} value={target} aria-label="Salário-base mensal"
              onChange={(event) => setTarget(Number(event.target.value) || 1518)}
            />
            <div className="hub-figures">
              <div className="hub-figure split">
                <span><Icon name="health_and_safety" size={14} />INSS</span>
                <strong>−{money(amountOf(result.data.deductions, 'INSS'))}</strong>
              </div>
              <div className="hub-figure split">
                <span><Icon name="account_balance" size={14} />IRRF</span>
                <strong>−{money(amountOf(result.data.deductions, 'IRRF'))}</strong>
              </div>
              {/* No mobile o protótipo junta as duas rubricas numa célula só. */}
              <div className="hub-figure merged">
                <span>INSS + IRRF</span>
                <strong>−{money(amountOf(result.data.deductions, 'INSS') + amountOf(result.data.deductions, 'IRRF'))}</strong>
              </div>
              <div className="hub-figure net">
                <span>{result.biweekly ? '2ª parcela' : 'Líquido estimado'}</span>
                <strong>{money(result.biweekly ? result.data.secondInstallment : result.data.net)}</strong>
              </div>
            </div>
          </div>

          <Link to="/calculos/salario" className="btn-contrast">
            <Icon name="tune" size={20} />Refinar com bônus, horas extras e descontos
          </Link>
        </div>
      </section>

      <section className="tools">
        <div className="tools-head" data-reveal>
          <h2>Todas as ferramentas</h2>
          <span><Icon name="update" size={18} />Competência 2026</span>
        </div>
        <div className="tools-grid">
          {tools.map((tool) => (
            <Link className="tool glass" to={tool.to} key={tool.to} data-reveal>
              <span className="tool-icon"><Icon name={tool.icon} size={28} /></span>
              <strong>{tool.title}</strong>
              <span className="tool-desc">{tool.description}</span>
            </Link>
          ))}
        </div>
      </section>

      <section className="family" id="brandnexx">
        <div className="family-head" data-reveal>
          <h2><Icon name="hub" size={26} />Conheça outros produtos da família brandnexx</h2>
          <a href="https://brandnexx.com" target="_blank" rel="noopener">brandnexx.com<Icon name="open_in_new" size={16} /></a>
        </div>
        <div className="family-grid">
          {family.map((product) => (
            <a className="family-card glass" key={product.name} href={product.url} target="_blank" rel="noopener" data-reveal>
              <span className="family-icon" style={{ background: product.tint, color: product.color }}>
                <Icon name={product.icon} size={24} />
              </span>
              <span className="family-arrow"><Icon name="arrow_outward" size={18} /></span>
              <span className="family-body">
                <h3>{product.name}</h3>
                <span className="family-tag" style={{ color: product.color }}>{product.tag}</span>
                <span className="family-summary">{product.short}</span>
                <p>{product.description}</p>
                <span className="family-host">{product.host}</span>
              </span>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
